import * as R from 'ramda';

import { Task, TaskField } from '@atom/types/task';

// Case sensitive names of custom fields for HIDOT `Overtime Planning` work template
export const HOURS_PER_RESOURCE = 'Hours per Resource';
export const REASONS_FOR_OVERTIME = 'Reason(s) for Overtime';
export const RAMIFICATIONS = 'Ramifications';
export const ALTERNATIVES = 'Alternatives';

export type OvertimeThreshold =
  | 'UP_TO_10'
  | 'UP_TO_20'
  | 'UP_TO_40'
  | 'OVER_40';

export type Signatory = {
  name: string;
  title: string;
};

export type OvertimeRouting = {
  to: string;
  from: string;
  through: string;
};

export type OvertimeApprovals = {
  recommendApproval: Signatory[];
  approver: Signatory;
};

const NAKATA: Signatory = {
  name: 'Ryan A. Nakata',
  title: 'Oahu District Engineer',
};

const DILL: Signatory = {
  name: 'Lawrence Dill',
  title: 'Highways Administrator',
};

const SHISHIDO: Signatory = {
  name: 'Robin K. Shishido',
  title: 'Deputy Director of Transportation for Highways',
};

const SNIFFEN: Signatory = {
  name: 'Edwin H. Sniffen',
  title: 'Director of Transportation',
};

const NAKATA_ROUTING = 'RYAN A. NAKATA, HWY-O';
const DILL_ROUTING = 'LAWRENCE DILL, HWY';
const SHISHIDO_ROUTING = 'ROBIN K. SHISHIDO, DEP-HWY';
const SNIFFEN_ROUTING = 'EDWIN H. SNIFFEN, Director of Transportation';

const THRESHOLD_LABELS: Record<OvertimeThreshold, string> = {
  UP_TO_10: 'Up to 10 hrs / mo / employee',
  UP_TO_20: 'Up to 20 hrs / mo / employee',
  UP_TO_40: 'Up to 40 hrs / mo / employee',
  OVER_40: 'More than 40 hrs / mo / employee',
};

const APPROVALS: Record<OvertimeThreshold, OvertimeApprovals> = {
  UP_TO_10: { recommendApproval: [], approver: NAKATA },
  UP_TO_20: { recommendApproval: [NAKATA], approver: DILL },
  UP_TO_40: { recommendApproval: [NAKATA, DILL], approver: SHISHIDO },
  OVER_40: {
    recommendApproval: [NAKATA, DILL, SHISHIDO],
    approver: SNIFFEN,
  },
};

export const getTaskField = (task: Task, title: string): TaskField =>
  R.find((field: TaskField) => field.title === title, task?.fields || []);

export const getHoursPerResource = (task: Task): number =>
  Number(getTaskField(task, HOURS_PER_RESOURCE)?.value) || 0;

export const getTotalOtHours = (task: Task): number =>
  getHoursPerResource(task) * R.length(task?.users || []);

export const getThreshold = (hoursPerResource: number): OvertimeThreshold => {
  if (!hoursPerResource || hoursPerResource <= 10) {
    return 'UP_TO_10';
  }

  if (hoursPerResource <= 20) {
    return 'UP_TO_20';
  }

  if (hoursPerResource <= 40) {
    return 'UP_TO_40';
  }

  return 'OVER_40';
};

export const getTaskThreshold = (task: Task): OvertimeThreshold =>
  getThreshold(getHoursPerResource(task));

export const getThresholdLabel = (threshold: OvertimeThreshold): string =>
  THRESHOLD_LABELS[threshold];

export const getRouting = (
  threshold: OvertimeThreshold,
  createdByName: string,
): OvertimeRouting => {
  switch (threshold) {
    case 'UP_TO_20':
      return {
        to: DILL_ROUTING,
        from: NAKATA_ROUTING,
        through: '-',
      };
    case 'UP_TO_40':
      return {
        to: SHISHIDO_ROUTING,
        from: NAKATA_ROUTING,
        through: DILL_ROUTING,
      };
    case 'OVER_40':
      return {
        to: SNIFFEN_ROUTING,
        from: NAKATA_ROUTING,
        through: `${SHISHIDO_ROUTING}, ${DILL_ROUTING}`,
      };
    default:
      return {
        to: NAKATA_ROUTING,
        from: createdByName || '',
        through: '-',
      };
  }
};

export const getApprovals = (threshold: OvertimeThreshold): OvertimeApprovals =>
  APPROVALS[threshold];
